import { ROUTES } from "@/constants/routes";
import { useIsCanvasCentricApp } from "@/context/CanvasCentricContext";
import { withSessionProvider } from "@/context/SessionContext";
import { useDictation } from "@/hooks/useDictation";
import { useMessageActions } from "@/hooks/useMessageActions";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import { ChevronLeft, Copy, Loader2, Mic, Send, Square } from "lucide-react";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { MobileScreen } from "./MobileScreen";

type Phase = "idle" | "recording" | "transcribing" | "review";

/**
 * Dictation-lite for mobile: record → one-shot transcribe → review, then either
 * hand the text off to a new chat (reusing useMessageActions, the same path the
 * home composer uses) or copy it. The live-voice seam (src/native/voice.ts) is
 * intentionally not surfaced here yet — it reports unavailable until the native
 * plugin + backend ship.
 */
function MobileVoicePageInner() {
  const navigate = useNavigate();
  const { isCanvasCentricApp } = useIsCanvasCentricApp();
  const [phase, setPhase] = useState<Phase>("idle");
  const [transcript, setTranscript] = useState("");

  // Scope carried over from a workspace-scoped mobile home ("Start chat"
  // lands the chat in that workspace); absent → private workspace.
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get("workspaceId") ?? undefined;

  const { onSendMessage, isLoading: isSending } = useMessageActions({
    workspaceId,
    onMessageSent: ({ flowSession }) => {
      // Hand off to the chat view of whichever shell is active: canvas-centric
      // opens the chat pane (`/chat/:id`); the legacy shell uses `/sessions/:id`.
      navigate(
        isCanvasCentricApp
          ? route(ROUTES.CHAT.DETAIL, { flowSessionId: flowSession.id })
          : route(ROUTES.SESSIONS.SESSION, { sessionId: flowSession.id }),
      );
    },
  });

  const {
    isRecording,
    isSupported,
    audioLevel,
    duration,
    interimText,
    startRecording,
    stopRecording,
  } = useDictation({
    onTranscriptionComplete: (text) => {
      if (text.trim()) {
        setTranscript(text);
        setPhase("review");
      } else {
        toast.error("No transcription text received.");
        setPhase("idle");
      }
    },
    onError: (err) => {
      toast.error(err.message);
      setPhase("idle");
    },
    // The duration cap stops recording inside the hook, never through stop()
    // below — advance the phase here or the UI stays stuck on "recording"
    // with a dead stop button until the transcript lands.
    onMaxDurationReached: () => setPhase("transcribing"),
  });

  const start = useCallback(async () => {
    setTranscript("");
    try {
      await startRecording();
      setPhase("recording");
    } catch {
      toast.error("Could not access the microphone.");
      setPhase("idle");
    }
  }, [startRecording]);

  const stop = useCallback(() => {
    if (isRecording) {
      setPhase("transcribing");
      stopRecording();
    }
  }, [isRecording, stopRecording]);

  const startChat = useCallback(async () => {
    const content = transcript.trim();
    if (!content) return;
    try {
      await onSendMessage({
        id: uuidv4(),
        content,
        file_record_ids: [],
        referenced_entities: [],
      });
    } catch {
      toast.error("Could not start the chat. Please try again.");
    }
  }, [transcript, onSendMessage]);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(transcript);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  }, [transcript]);

  const mmss = `${String(Math.floor(duration / 60)).padStart(2, "0")}:${String(
    duration % 60,
  ).padStart(2, "0")}`;
  const bars = Array.from({ length: 5 });
  const barHeights = bars.map((_, i) =>
    Math.max(6, Math.round(audioLevel * 40 * (Math.sin(i * 0.8) * 0.3 + 0.7))),
  );

  const secondaryBtn =
    "flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-medium text-foreground active:bg-accent";
  const primaryBtn =
    "flex flex-1 items-center justify-center gap-2 rounded-xl bg-rust px-4 py-3 text-sm font-medium text-white disabled:opacity-50";

  return (
    <MobileScreen>
      <div className="flex min-h-0 w-full flex-1 flex-col py-3">
        <div className="flex items-center gap-2 pb-2">
          <button
            type="button"
            onClick={() =>
              // Return to the surface that launched us — the scoped
              // workspace home when a scope came along, `/m` otherwise.
              navigate(
                workspaceId
                  ? route(ROUTES.WORKSPACES.DETAIL, { workspaceId })
                  : ROUTES.MOBILE.HOME,
              )
            }
            className="-ml-2 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-accent"
            aria-label="Back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">Voice</h1>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center gap-6 pb-safe">
          {!isSupported ? (
            <p className="max-w-xs text-center text-sm text-muted-foreground">
              Voice recording isn&apos;t supported on this device.
            </p>
          ) : phase === "review" ? (
            <div className="flex w-full max-w-md flex-1 flex-col gap-4 py-4">
              <textarea
                value={transcript}
                onChange={(e) => setTranscript(e.target.value)}
                className="min-h-[160px] flex-1 resize-none rounded-xl border bg-background p-4 text-base outline-none focus:ring-2 focus:ring-rust"
                placeholder="Your dictation…"
                autoFocus
              />
              <div className="flex items-center gap-3">
                <button type="button" onClick={copy} className={secondaryBtn}>
                  <Copy className="h-4 w-4" /> Copy
                </button>
                <button type="button" onClick={start} className={secondaryBtn}>
                  <Mic className="h-4 w-4" /> Redo
                </button>
                <button
                  type="button"
                  onClick={startChat}
                  disabled={isSending || !transcript.trim()}
                  className={primaryBtn}
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  Start chat
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                type="button"
                onClick={phase === "recording" ? stop : start}
                disabled={phase === "transcribing"}
                className={cn(
                  "flex h-28 w-28 items-center justify-center rounded-full shadow-lg transition-all active:scale-95",
                  phase === "recording"
                    ? "bg-lc-blocked/20 text-lc-blocked"
                    : "bg-rust text-white",
                  phase === "transcribing" && "opacity-60",
                )}
                aria-label={phase === "recording" ? "Stop recording" : "Record"}
              >
                {phase === "transcribing" ? (
                  <Loader2 className="h-10 w-10 animate-spin" />
                ) : phase === "recording" ? (
                  <Square className="h-9 w-9 fill-current" />
                ) : (
                  <Mic className="h-10 w-10" />
                )}
              </button>

              {phase === "recording" ? (
                <div className="flex flex-col items-center gap-3">
                  <div className="flex h-10 items-end gap-1">
                    {bars.map((_, i) => (
                      <div
                        key={i}
                        className="w-1.5 rounded-full bg-foreground/70 transition-[height] duration-100"
                        style={{ height: barHeights[i] }}
                      />
                    ))}
                  </div>
                  <span className="font-mono text-sm tabular-nums text-muted-foreground">
                    {mmss}
                  </span>
                  {interimText && (
                    <p className="max-w-sm px-6 text-center text-sm text-muted-foreground line-clamp-3">
                      {interimText}
                    </p>
                  )}
                  <span className="text-xs text-muted-foreground">
                    Tap to stop
                  </span>
                </div>
              ) : phase === "transcribing" ? (
                <span className="text-sm text-muted-foreground">
                  Transcribing…
                </span>
              ) : (
                <p className="max-w-xs text-center text-sm text-muted-foreground">
                  Tap to dictate. We&apos;ll transcribe it, then you can start a
                  chat.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </MobileScreen>
  );
}

export const MobileVoicePage = withSessionProvider(MobileVoicePageInner);
export default MobileVoicePage;
